import Checkbox, { CheckboxProps } from "@mui/material/Checkbox";
import InputBase from "@mui/material/InputBase";
import Radio, { RadioProps } from "@mui/material/Radio";
import { createStyles } from "@mui/styles";
import {
  Theme,
  Tooltip,
  TooltipProps,
  styled,
  tooltipClasses,
} from "@mui/material";
import Switch from "@mui/material/Switch";
import { CSSProperties, withStyles } from "@mui/styles";
import { createTheme } from "@mui/material/styles";

const borderRadius = "30px";
const primaryColor = "#0d3057";
const primaryBackgroundColor = "#000000";
const borderColor = "rgba(0, 0, 0, 0.12)";
const borderStyle = "1px solid " + borderColor;
const infoTextColor = "#888888";
const disabledBackgroundColor = "#888888";
const defaultFontSize = 14;
const defaultBoxShadow = "0 0 0 0.2rem rgb(0 123 255 / 25%)";
const drawerWidth = 300;
const textLightColor = "#FFFFFF";
const purplePrimaryColor = "#000000";
const pinkDarkColor = "#c11986";
const pureWhiteColor = "#ffffff";
const primaryHeadingColor = "#001529";
const primaryGreenColor = "#adc804";
const primaryBlackColor = "#000000";
const primaryBlue = "#1a0224";
const lightTextColor = "#666";
const lightPinkColor = "#FCF5FF";
const activeMenuColor = "#1A0224";
const darkPurpledColor = "#4b0150";
const inputLabelRequiredColor = "red";
const buttonWhiteBg = "#E7E7E7";
const lightBgColor = "#FAF5FD";
const chipBackgroundColor = "#FFF1F9";
const completeChipBackgroundColor = "#E7EEBD";
const cardBorderColor = "#f0f5f5";
const warningColor = "red";

const mainContainer: CSSProperties = {
  margin: "20px",
};
const mainFlexContainer: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "space-between",
};
const blackFont: CSSProperties = {
  fontFamily: "Poppins_Black",
  fontWeight: 900,
  fontStyle: "normal",
};

const boldFont: CSSProperties = {
  fontFamily: "Poppins_Bold",
  fontWeight: 700,
};
const leftItemFlex: CSSProperties = {
  display: "flex",
  alignItems: "left",
  justifyContent: "left",
};

const mediumFont: CSSProperties = {
  fontFamily: "Poppins_Medium",
  fontWeight: 500,
};
const rowItemFlex: CSSProperties = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
};
const regularFont: CSSProperties = {
  fontFamily: "Poppins_Regular",
  fontWeight: 400,
};

const theme = createTheme({
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      "Poppins_Regular",
      "Poppins_Medium",
      "Poppins_Bold",
      "Poppins_Black",
      "sans-serif",
    ].join(","),
  },
});

const getRelativeFontSize = (value: number = 0) => {
  let size = defaultFontSize + value;
  return size + "px";
};

const customButtonStyle: CSSProperties = {
  borderRadius: "14px",
  border: "none",
  fontSize: getRelativeFontSize(),
  textAlign: "center",
  backgroundColor: purplePrimaryColor,
  padding: "10px 15px",
  boxShadow: "0px 3px 17px rgba(0, 0, 0, 0.07)",
  color: "#f15e22",
  cursor: "pointer",
  textTransform: "none",
  height: "47px",
  transition: "all .3s ease",
  "&:hover": {
    background: purplePrimaryColor,
  },
};

const customTextFieldStyle: CSSProperties = {
  borderRadius: borderRadius,
  position: "relative",
  border: "none",
  fontSize: getRelativeFontSize(2),
  backgroundColor: primaryBackgroundColor,
  padding: "10px 15px",
  boxShadow: "none",
  width: "100%",
};

const headingText: CSSProperties = {
  display: "inline-block",
  fontSize: getRelativeFontSize(8),
};

const centerItemFlex: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const centerItemAbsolute: CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%,-50%)",
};

const CustomInput = withStyles((theme: Theme) =>
  createStyles({
    root: {
      "label + &": {
        marginTop: theme.spacing(3),
      },
      backgroundColor: theme.palette.common.black,
      border: "1px solid #ced4da",
      borderRadius: borderRadius,
      padding: "0 5px",
    },
    input: {
      position: "relative",
      fontSize: getRelativeFontSize(),
      width: "100%",
      padding: "10px 12px",
    },
  })
)(InputBase);

const CustomSwitch = withStyles({
  switchBase: {
    color: "grey",
    "&$checked": {
      color: primaryColor,
      "& + $track": {
        backgroundColor: primaryColor,
      },
    },
    "&$checked + $track": {
      color: primaryColor,
    },
  },
  track: { backgroundColor: "grey" },
  checked: {},
})(Switch);

const CustomCheckbox = withStyles({
  root: {
    color: primaryColor,
    "&$checked": {
      color: primaryColor,
    },
  },
  checked: {},
})((props: CheckboxProps) => <Checkbox color="default" {...props} />);

const CustomRadio = withStyles({
  root: {
    color: primaryColor,
    "&$checked": {
      color: primaryColor,
    },
  },
  checked: {},
})((props: RadioProps) => <Radio color="default" {...props} />);

export const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#FFFFFF",
    color: "#000000",
    boxShadow: theme.shadows[1],
    fontSize: 11,
    ...regularFont,
  },
}));

export const BootstrapInput = withStyles((theme: Theme) =>
  createStyles({
    root: {
      "label + &": {
        marginTop: theme.spacing(4),
      },
      backgroundColor: theme.palette.common.white,
      border: "1px solid #ced4da",
      borderRadius: "15px",
      padding: "5px 5px",
      width: "300px",
    },
    input: {
      position: "relative",
      fontSize: getRelativeFontSize(),
      width: "100%",
      padding: "10px 12px",
      "&::placeholder ": {
        ...regularFont,
      },
    },
  })
)(InputBase);

export {
  borderRadius,
  primaryColor,
  disabledBackgroundColor,
  primaryBackgroundColor,
  borderColor,
  borderStyle,
  infoTextColor,
  defaultBoxShadow,
  customButtonStyle,
  customTextFieldStyle,
  mainFlexContainer,
  rowItemFlex,
  leftItemFlex,
  headingText,
  centerItemFlex,
  centerItemAbsolute,
  CustomInput,
  CustomSwitch,
  CustomCheckbox,
  CustomRadio,
  regularFont,
  blackFont,
  boldFont,
  mediumFont,
  getRelativeFontSize,
  theme,
  mainContainer,
  drawerWidth,
  textLightColor,
  purplePrimaryColor,
  pinkDarkColor,
  pureWhiteColor,
  primaryHeadingColor,
  primaryGreenColor,
  primaryBlue,
  lightTextColor,
  primaryBlackColor,
  lightPinkColor,
  activeMenuColor,
  darkPurpledColor,
  inputLabelRequiredColor,
  buttonWhiteBg,
  lightBgColor,
  chipBackgroundColor,
  completeChipBackgroundColor,
  warningColor,
  cardBorderColor,
};
